import { Injectable, inject, signal } from '@angular/core';
import { ShiveCurrencyPipe } from '../../../../../core/pipes/shive-currency.pipe';
import { ShiveRenderService } from '../../../../../core/services/rendering/shive-render.service';
import { DoughnutData } from '../../../../../shared/data-types/chart-types';
import { DateTimeHelper } from '../../../../../features/calendar/util/date-time-helper';
import { ProjectModel } from '../../../../../core/models/project/project.model';
import { BudgetCalculationService } from '../../project-edit/manage-project/project-calculation-form/services/budget-calculation.service';
import { ExpenditureCalculationService } from '../../project-edit/manage-project/project-calculation-form/services/expenditure-calculation.service';
import { TimeBlockDurationCalculationService } from '../../../../../features/calendar/components/time-block/calculation/duration/time-block-duration-calculation.service';
import { TimeBlockWithTask } from '../../../../../shared/data-types/shared-time-block-types';
import { TimeBlockProjectModel } from '../../../../../core/models/timeblock/time-block.model';
import { ProjectMonetaryCalculationService } from '../services/project-monetary-calculation.service';
import { TimeBlockFeeCalculationService } from '../services/time-block-fee-calculation.service';
import { FeeUnit } from '../../../../../shared/data-types/unit-types';

@Injectable({
  providedIn: 'root',
})
export class ProjectSummaryAsideService {
  billableTime = signal(-1);
  nonBillableTime = signal(-1);
  unit = signal<FeeUnit>('monetary');
  totalBudget = signal(-1);
  expendituresAbsolut = signal(-1);
  expenses = signal(-1);
  personnelCosts = signal(-1);

  // DI
  private readonly shiveCurrencyPipe = inject(ShiveCurrencyPipe);
  private readonly expenditureCalculationService = inject(ExpenditureCalculationService);
  private readonly timeBlockDurationCalculationService = inject(
    TimeBlockDurationCalculationService,
  );
  private readonly budgetService = inject(BudgetCalculationService);
  private readonly projectMonetaryCalculationService = inject(ProjectMonetaryCalculationService);
  private readonly timeBlockFeeCalculationService = inject(TimeBlockFeeCalculationService);
  private readonly shiveRenderService = inject(ShiveRenderService);

  // ---------- Project Time ----------
  public calcBillableTime(timeBlocksWithTask: TimeBlockWithTask[]): void {
    const billableTimeBlocks = timeBlocksWithTask
      .filter((timeBlock) => timeBlock.task.isBillable)
      .map((timeBlock) => timeBlock.timeBlock);
    const billableSeconds =
      this.timeBlockDurationCalculationService.getTimeInRangeForTimeBlocks(billableTimeBlocks);
    this.billableTime.set(billableSeconds);

    const nonBillableTimeBlocks = timeBlocksWithTask
      .filter((timeBlock) => !timeBlock.task.isBillable)
      .map((timeBlock) => timeBlock.timeBlock);
    const nonBillableSeconds =
      this.timeBlockDurationCalculationService.getTimeInRangeForTimeBlocks(nonBillableTimeBlocks);
    this.nonBillableTime.set(nonBillableSeconds);
  }

  // ---------- Billable / non-billable ----------
  public buildBillableDoughnutData(
    billableSeconds: number,
    nonBillableSeconds: number,
    totalSeconds: number,
  ): DoughnutData[] {
    const total = totalSeconds > 0 ? totalSeconds : 0;
    let doughnutFirstValueColor = this.shiveRenderService.getCSSVar('--doughnut-first-value');

    if (total === 0) {
      doughnutFirstValueColor = this.shiveRenderService.getCSSVar('--doughnut-empty');
    }

    return [
      {
        kind: 'Verrechenbar',
        share: total > 0 ? billableSeconds / total : 100, // If total is 0, show full gray circle.
        color: doughnutFirstValueColor,
        value: `${DateTimeHelper.formatSecondsToHoursAndMinutes(billableSeconds)} h`,
      },
      {
        kind: 'Nicht verrechenbar',
        share: total > 0 ? nonBillableSeconds / total : 0,
        color: this.shiveRenderService.getCSSVar('--doughnut-second-value'),
        value: `${DateTimeHelper.formatSecondsToHoursAndMinutes(nonBillableSeconds)} h`,
      },
    ];
  }

  public buildBillableDoughnutLabels(
    billableSeconds: number,
    nonBillableSeconds: number,
    totalSeconds: number,
  ): DoughnutData[] {
    const data = this.buildBillableDoughnutData(billableSeconds, nonBillableSeconds, totalSeconds);

    return [
      ...data,
      {
        value: `${DateTimeHelper.formatSecondsToHoursAndMinutes(totalSeconds)} h`,
      },
    ];
  }

  public calcTotalInvoiceAmount(
    timeBlocksWithTask: TimeBlockWithTask[],
    projectClone: ProjectModel,
    alreadyInvoiced: boolean,
  ): number {
    let timeBlockFees = this.timeBlockFeeCalculationService.timeBlockFeesOfProject(
      projectClone,
      timeBlocksWithTask,
      true,
      { start: null, end: null },
    );

    timeBlockFees = timeBlockFees.filter((fee) => {
      const invoiceId = (fee.timeBlockWithTask.timeBlock.timeBlockModel as TimeBlockProjectModel)
        .invoiceId;
      const isBillable = fee.timeBlockWithTask.task.isBillable;
      return (invoiceId !== null) === alreadyInvoiced && isBillable;
    });

    return timeBlockFees.reduce((acc, cur) => {
      return this.projectMonetaryCalculationService.calculateFeeSum([cur]) + acc;
    }, 0);
  }

  // ---------- Costs (expenses + personnel costs) ----------
  public calcTotalProjectCosts(timeBlocksWithTask: TimeBlockWithTask[]): void {
    const totalPersonnelCosts =
      this.projectMonetaryCalculationService.calcPersonnelCosts(timeBlocksWithTask);

    this.personnelCosts.set(totalPersonnelCosts);
    this.expenses.set(40); // Todo: SW-284
  }

  public buildCostDoughnutData(expenses: number, personnelCosts: number): DoughnutData[] {
    const total = expenses + personnelCosts;
    let doughnutFirstValueColor = this.shiveRenderService.getCSSVar('--doughnut-first-value');

    if (total === 0) {
      doughnutFirstValueColor = this.shiveRenderService.getCSSVar('--doughnut-empty');
    }

    return [
      {
        kind: 'Ausgaben',
        share: total > 0 ? expenses / total : 100, // If total is 0, show full gray circle.
        color: doughnutFirstValueColor,
        value: this.shiveCurrencyPipe.transform(expenses),
      },
      {
        kind: 'Mitarbeiter',
        share: total > 0 ? personnelCosts / total : 0,
        color: this.shiveRenderService.getCSSVar('--doughnut-second-value'),
        value: this.shiveCurrencyPipe.transform(personnelCosts),
      },
    ];
  }

  public buildCostDoughnutLabels(expenses: number, expenditures: number): DoughnutData[] {
    const data = this.buildCostDoughnutData(expenses, expenditures);
    const total = expenses + expenditures;

    return [
      ...data,
      {
        value: this.shiveCurrencyPipe.transform(total),
      },
    ];
  }

  // ---------- Remaining budget ----------
  public calcRemainingBudget(project: ProjectModel, timeBlocksWithTask: TimeBlockWithTask[]): void {
    const budget = this.budgetService.calculateBudget(project);
    const unit = BudgetCalculationService.getFeeUnit(project);
    this.totalBudget.set(budget);
    this.unit.set(unit);

    const timeBlocks = timeBlocksWithTask.map((timeBlock) => timeBlock.timeBlock);
    let expenditures = 0;
    if (unit === 'monetary') {
      expenditures = this.expenditureCalculationService.calcFeeSumForTimeBlock(
        project,
        timeBlocksWithTask,
      );
    } else {
      expenditures = this.timeBlockDurationCalculationService.calculateTimeBlockTimeSum(
        timeBlocks,
        true,
      );
    }

    this.expendituresAbsolut.set(expenditures);
  }
}
